import React, { type ReactElement } from 'react';
import { useFormContext } from 'react-hook-form';
import ErrorMessage from 'atom-components/error-message-from-hook-form';
import Checkbox from 'atom-components/form-fields-for-hook-form/checkbox';
import FormLabelAndInputContainer from 'atom-components/form-label-and-input-container';
import Label from 'atom-components/label';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import GroupMemberMarkerBadge from './group-member-marker-badge';
import type { GroupMemberFormValue, GroupMemberType } from './marking-group-form.schema';

export type GroupMembersProps = {
  groupMemberType: GroupMemberType;
  organisationUsers: BaseCustomerUser[];
};

const partialFieldName = 'GroupMembers';
const partialFieldLabel = 'Group Members';

export default function GroupMembers({
  groupMemberType,
  organisationUsers
}: GroupMembersProps): ReactElement {
  const { watch, control, formState } = useFormContext();

  const fieldName = `${groupMemberType}${partialFieldName}`;
  const fieldLabel = `${partialFieldLabel} (${groupMemberType === 'nonMarker' ? 'Non-' : ''}Markers)`;

  // Loads a different groupMembers field based on groupMemberType:
  const groupMembers: GroupMemberFormValue[] = watch(fieldName);

  return (
    <FormLabelAndInputContainer>
      <Label htmlFor={fieldName}>{fieldLabel}</Label>
      {groupMembers.length > 0 && (
        <ul className="list-unstyled">
          {groupMembers.map((groupMember, index) => (
            <li key={groupMember.organisationUserId}>
              <Checkbox role="checkbox">
                <Checkbox.Input
                  id={`organisationUser-${groupMember.organisationUserId}`}
                  control={control}
                  name={`${fieldName}.${index}.checked`}
                  role="checkbox"
                />
                <Checkbox.Label htmlFor={`organisationUser-${groupMember.organisationUserId}`}>
                  <GroupMemberMarkerBadge
                    groupMember={groupMember}
                    groupMemberType={groupMemberType}
                    organisationUsers={organisationUsers}
                  />
                </Checkbox.Label>
              </Checkbox>
            </li>
          ))}
        </ul>
      )}
      {formState.errors.groupMembers && (
        <ErrorMessage errors={formState.errors} name={`${fieldName}.root`} />
      )}
    </FormLabelAndInputContainer>
  );
}
